import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";
import { MdAdd, MdDelete } from "react-icons/md";

const NewPurchaseOrder = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  const [items, setItems] = useState([{ item: "", qty: 1, unitPrice: 0 }]);

  const [vendors, setVendors] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [vendorsRes, itemsRes] = await Promise.all([
        fetch("/api/getAllVendors?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllInventoryItems?limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const vendorsData = await vendorsRes.json();
      const itemsData = await itemsRes.json();

      if (vendorsRes.ok) setVendors(vendorsData.vendors || []);
      if (itemsRes.ok) setInventoryItems(itemsData.items || []);
    } catch {
      setErrorMessage("Failed to load form data");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const addLineItem = () => {
    setItems([...items, { item: "", qty: 1, unitPrice: 0 }]);
  };

  const removeLineItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...items];
    if (field === "item") {
      updated[index].item = value;
      const invItem = inventoryItems.find((i) => i._id === value);
      if (invItem) {
        updated[index].unitPrice = invItem.unitCost || 0;
      }
    } else {
      updated[index][field] = value;
    }
    setItems(updated);
  };

  const getLineTotal = (lineItem) => {
    return (parseFloat(lineItem.qty) || 0) * (parseFloat(lineItem.unitPrice) || 0);
  };

  const getSubTotal = () => {
    return items.reduce((sum, item) => sum + getLineTotal(item), 0);
  };

  const getTaxAmount = () => {
    return getSubTotal() * ((parseFloat(taxRate) || 0) / 100);
  };

  const getTotalAmount = () => {
    return getSubTotal() + getTaxAmount();
  };

  const validateForm = () => {
    if (!vendor) {
      setErrorMessage("Please select a vendor");
      return false;
    }
    if (items.some((i) => !i.item || i.qty < 1)) {
      setErrorMessage("Each item must be selected with quantity >= 1");
      return false;
    }
    return true;
  };

  const handleSubmit = async (asDraft = true) => {
    if (!validateForm()) {
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/addPurchaseOrder", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vendor,
          items: items.map((i) => ({
            item: i.item,
            qty: parseInt(i.qty),
            unitPrice: parseFloat(i.unitPrice),
          })),
          expectedDeliveryDate: expectedDeliveryDate || undefined,
          notes,
          taxRate: parseFloat(taxRate) || 0,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || "Failed to create purchase order");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      if (!asDraft && data.purchaseOrder?._id) {
        const submitRes = await fetch(`/api/purchaseOrder/submit/${data.purchaseOrder._id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!submitRes.ok) {
          const submitData = await submitRes.json();
          setErrorMessage(submitData.error || "Created but failed to submit for approval");
          setTimeout(() => setErrorMessage(""), 3000);
          setLoading(false);
          return;
        }
      }

      setSuccessMessage(
        asDraft ? "Purchase order saved as draft" : "Purchase order submitted for approval"
      );
      setTimeout(() => {
        navigate("/PurchaseOrders");
      }, 1500);
    } catch {
      setErrorMessage("Failed to create purchase order");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/PurchaseOrders")}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">New Purchase Order</h2>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        {/* Vendor, Expected Delivery, Tax Rate */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Vendor <span className="text-red-500">*</span>
            </label>
            <select
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Vendor</option>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>{v.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Expected Delivery Date
            </label>
            <input
              type="date"
              value={expectedDeliveryDate}
              onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Tax Rate (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              Line Items <span className="text-red-500">*</span>
            </label>
            <button
              onClick={addLineItem}
              className="flex items-center gap-1 text-primary text-sm font-medium hover:underline"
            >
              <MdAdd /> Add Item
            </button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Unit Price (Nu)</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Line Total (Nu)</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((lineItem, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2">
                        <select
                          value={lineItem.item}
                          onChange={(e) => updateLineItem(index, "item", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="">Select item</option>
                          {inventoryItems.map((inv) => (
                            <option key={inv._id} value={inv._id}>
                              {inv.sku} - {inv.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="1"
                          value={lineItem.qty}
                          onChange={(e) => updateLineItem(index, "qty", e.target.value)}
                          className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={lineItem.unitPrice}
                          onChange={(e) => updateLineItem(index, "unitPrice", e.target.value)}
                          className="w-28 border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm font-medium">
                        Nu. {getLineTotal(lineItem).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        {items.length > 1 && (
                          <button
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <MdDelete />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex flex-col items-end mt-3 space-y-1">
            <p className="text-sm text-gray-600">
              Subtotal: <span className="font-medium">Nu. {getSubTotal().toLocaleString()}</span>
            </p>
            <p className="text-sm text-gray-600">
              Tax ({taxRate || 0}%): <span className="font-medium">Nu. {getTaxAmount().toLocaleString()}</span>
            </p>
            <p className="text-sm font-semibold text-gray-800">
              Total: <span className="text-primary">Nu. {getTotalAmount().toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block font-medium mb-2 text-blue-500">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional notes for this purchase order"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/PurchaseOrders")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={loading}
            className="px-4 py-2 border border-primary text-primary rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save as Draft"}
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Submitting..." : "Submit for Approval"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewPurchaseOrder;
